import { useState, useEffect, useRef } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { LANGUAGES } from "@/lib/i18n";
import { supabase } from "@/integrations/supabase/client";

// Global in-memory cache keyed by cachePrefix-lang
const translationCache: Record<string, string[]> = {};

/**
 * Hook to translate an array of texts using the AI translation edge function.
 * Returns the original texts if the language is English.
 * Caches results in memory so translation only happens once per key+language.
 */
export function useTranslatedTexts(
  texts: string[],
  cachePrefix: string
): { translated: string[]; isTranslating: boolean } {
  const { language } = useLanguage();
  const [translated, setTranslated] = useState<string[]>(texts);
  const [isTranslating, setIsTranslating] = useState(false);
  const textsRef = useRef(texts);
  textsRef.current = texts;

  useEffect(() => {
    if (language === "en") {
      setTranslated(textsRef.current);
      return;
    }

    const cacheKey = `${cachePrefix}-${language}`;
    if (translationCache[cacheKey]) {
      setTranslated(translationCache[cacheKey]);
      return;
    }

    let cancelled = false;

    const doTranslate = async () => {
      setIsTranslating(true);
      try {
        const langInfo = LANGUAGES.find((l) => l.code === language);

        // Split into batches and run ALL in parallel
        const batchSize = 50;
        const allTexts = textsRef.current;
        const batches: string[][] = [];
        for (let i = 0; i < allTexts.length; i += batchSize) {
          batches.push(allTexts.slice(i, i + batchSize));
        }

        const batchResults = await Promise.all(
          batches.map(async (batch) => {
            const { data, error } = await supabase.functions.invoke("translate", {
              body: {
                texts: batch,
                targetLanguage: language,
                targetLanguageName: langInfo?.label || language,
              },
            });
            if (error) throw error;
            return data?.translations && Array.isArray(data.translations)
              ? data.translations
              : batch;
          })
        );

        if (cancelled) return;
        const results = batchResults.flat();

        if (!cancelled) {
          translationCache[cacheKey] = results;
          setTranslated(results);
        }
      } catch (err) {
        console.error(`Translation failed for ${cachePrefix}:`, err);
        if (!cancelled) {
          setTranslated(textsRef.current);
        }
      } finally {
        if (!cancelled) setIsTranslating(false);
      }
    };

    doTranslate();
    return () => { cancelled = true; };
  }, [language, cachePrefix]);

  return { translated, isTranslating };
}
